/**
 * Airdrop Context
 * Manages airdrop state, WebSocket updates, and user interactions
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useWallet } from './WalletProvider';
import * as airdropApi from '../services/airdropApiService';
import { bettingWebSocketService } from '../services/BettingWebSocketService';
import twitterOAuthService from '../services/twitterOAuthService';

interface AirdropRound {
  hasActiveRound?: boolean;
  roundId: string;
  roundStart: string;
  roundEnd: string;
  totalPool: number;
  eligibleUsersCount: number;
  totalBetsCount: number;
  status: string;
  timeRemainingSeconds: number;
}

interface UserEligibility {
  hasActiveRound: boolean;
  isEligible: boolean;
  betCount: number;
  totalVolume: number;
  totalEligibleVolume?: number;
  hasSocialConnected: boolean;
  missingCriteria: string[];
}

interface TwitterConnection {
  connected: boolean;
  username?: string;
}

interface AirdropContextType {
  // Current round data
  currentRound: AirdropRound | null;
  
  // User eligibility
  userEligibility: UserEligibility | null;
  
  // Twitter connection
  twitterConnection: TwitterConnection;
  
  // Loading states
  loading: {
    round: boolean;
    eligibility: boolean;
    twitter: boolean;
  };
  
  // Actions
  refreshRound: () => Promise<void>;
  refreshEligibility: () => Promise<void>;
  connectTwitter: () => Promise<void>;
  disconnectTwitter: () => Promise<void>;
}

const AirdropContext = createContext<AirdropContextType | undefined>(undefined);

export function AirdropProvider({ children }: { children: React.ReactNode }) {
  const { address: wallet, isAuthenticated: authenticated } = useWallet();
  
  // State
  const [currentRound, setCurrentRound] = useState<AirdropRound | null>(null);
  const [userEligibility, setUserEligibility] = useState<UserEligibility | null>(null);
  const [twitterConnection, setTwitterConnection] = useState<TwitterConnection>({
    connected: false
  });
  const [loading, setLoading] = useState({
    round: false,
    eligibility: false,
    twitter: false
  });
  
  /**
   * Fetch current airdrop round
   */
  const refreshRound = useCallback(async () => {
    setLoading(prev => ({ ...prev, round: true }));
    try {
      const result = await airdropApi.getCurrentRound();
      if (result.success && result.data) {
        setCurrentRound(result.data);
      } else {
        setCurrentRound(null);
      }
    } catch (error) {
      console.error('❌ AIRDROP: Error refreshing round:', error);
    } finally {
      setLoading(prev => ({ ...prev, round: false }));
    }
  }, []);
  
  /**
   * Fetch user eligibility status
   */
  const refreshEligibility = useCallback(async () => {
    if (!wallet) {
      setUserEligibility(null);
      setTwitterConnection({ connected: false });
      return;
    }
    
    setLoading(prev => ({ ...prev, eligibility: true }));
    try {
      const result = await airdropApi.getUserEligibility(wallet);
      if (result.success && result.data) {
        setUserEligibility(result.data);
        // Update twitter connection from eligibility data
        setTwitterConnection({
          connected: result.data.hasSocialConnected,
          username: undefined // We can enhance this later if needed
        });
      }
    } catch (error) {
      console.error('❌ AIRDROP: Error refreshing eligibility:', error);
    } finally {
      setLoading(prev => ({ ...prev, eligibility: false }));
    }
  }, [wallet]);
  
  /**
   * Twitter OAuth flow
   */
  const connectTwitter = useCallback(async () => {
    if (!wallet) {
      console.error('No wallet connected');
      return;
    }
    
    try {
      // Initialize OAuth service with client ID from env
      const clientId = process.env.REACT_APP_TWITTER_CLIENT_ID;
      if (!clientId) {
        console.error('Twitter Client ID not configured');
        alert('Twitter integration not configured. Please contact support.');
        return;
      }
      
      twitterOAuthService.initialize(clientId);
      
      // Start OAuth flow (will redirect to Twitter)
      await twitterOAuthService.startOAuthFlow(wallet);
    } catch (error) {
      console.error('Error starting Twitter OAuth:', error);
      alert('Failed to connect Twitter. Please try again.');
    }
  }, [wallet]);
  
  /**
   * Disconnect Twitter account
   */
  const disconnectTwitter = useCallback(async () => {
    if (!wallet) return;
    
    setLoading(prev => ({ ...prev, twitter: true }));
    try {
      const result = await airdropApi.disconnectSocial(wallet, 'twitter');
      if (result.success) {
        setTwitterConnection({ connected: false });
        await refreshEligibility(); // Refresh to update eligibility
      }
    } catch (error) {
      console.error('❌ AIRDROP: Error disconnecting Twitter:', error);
    } finally {
      setLoading(prev => ({ ...prev, twitter: false }));
    }
  }, [wallet, refreshEligibility]);
  
  /**
   * WebSocket event handlers
   */
  const handleAirdropEvent = useCallback((event: { type: string; data: any }) => {
    // Only handle new round events - that's all we care about
    if (event.type === 'newRound') {
      console.log('🎁 AIRDROP: New round started', event.data);
      setCurrentRound(event.data);
      refreshEligibility(); // Refresh eligibility for new round
    }
  }, [refreshEligibility]);
  
  /**
   * Subscribe to WebSocket events
   */
  useEffect(() => {
    bettingWebSocketService.addAirdropCallback(handleAirdropEvent);
    
    return () => {
      bettingWebSocketService.removeAirdropCallback(handleAirdropEvent);
    };
  }, [handleAirdropEvent]);
  
  /**
   * Subscribe to bet placed events to refresh eligibility
   */
  useEffect(() => {
    if (!wallet) return;
    
    const handleBetPlaced = (betPlacedUpdate: any) => {
      // Only refresh if this is the user's bet
      if (betPlacedUpdate.data.userWallet === wallet) {
        console.log('🎲 AIRDROP: User placed bet, refreshing eligibility');
        refreshEligibility();
      }
    };
    
    bettingWebSocketService.addBetPlacedCallback(handleBetPlaced);
    
    return () => {
      bettingWebSocketService.removeBetPlacedCallback(handleBetPlaced);
    };
  }, [wallet, refreshEligibility]);
  
  /**
   * Handle Twitter OAuth callback on mount
   */
  useEffect(() => {
    const handleTwitterCallback = async () => {
      const callbackData = twitterOAuthService.handleCallback();
      if (!callbackData) {
        return;
      }

      const { code, verifier, walletAddress } = callbackData;
      
      setLoading(prev => ({ ...prev, twitter: true }));
      try {
        // Send to backend for verification
        const result = await airdropApi.connectTwitter(walletAddress, code, verifier);
        
        if (result.success) {
          console.log('✅ Twitter connected successfully');
          setTwitterConnection({
            connected: true,
            username: result.data?.username
          });
          await refreshEligibility();
        } else {
          console.error('Failed to connect Twitter:', result.error);
          alert('Failed to connect Twitter. Please try again.');
        }
      } catch (error) {
        console.error('Error completing Twitter connection:', error);
        alert('Failed to connect Twitter. Please try again.');
      } finally {
        twitterOAuthService.cleanup();
        setLoading(prev => ({ ...prev, twitter: false }));
      }
    };

    handleTwitterCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  /**
   * Initial data load
   */
  useEffect(() => {
    refreshRound();
  }, [refreshRound]);
  
  /**
   * Load user eligibility when wallet changes
   */
  useEffect(() => {
    if (authenticated && wallet) {
      refreshEligibility();
    }
  }, [authenticated, wallet, refreshEligibility]);
  
  const value: AirdropContextType = {
    currentRound,
    userEligibility,
    twitterConnection,
    loading,
    refreshRound,
    refreshEligibility,
    connectTwitter,
    disconnectTwitter
  };
  
  return (
    <AirdropContext.Provider value={value}>
      {children}
    </AirdropContext.Provider>
  );
}

export function useAirdrop() {
  const context = useContext(AirdropContext);
  if (context === undefined) {
    throw new Error('useAirdrop must be used within AirdropProvider');
  }
  return context;
}

export default AirdropContext;

